/**
 * SpotifyQueueBadge - Indicador compacto del header
 *
 * Muestra el nº de canciones en cola de Spotify y, al hacer hover/tap,
 * despliega un popover con las próximas 5.
 *
 * Se oculta si Spotify no está conectado.
 * Creado: 2026-04-20 (enhancement tras fix de queue/add).
 */

import { ListMusic } from 'lucide-react';
import { useSpotify } from '../SpotifyContext';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from './ui/popover';

export const SpotifyQueueBadge = () => {
  const { isConnected, queueCount, upNext } = useSpotify();

  if (!isConnected) return null;

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid="spotify-queue-badge"
          className="flex items-center gap-1.5 px-2 sm:px-2.5 py-1 rounded-full bg-green-600/20 hover:bg-green-600/40 border border-green-500/30 text-green-300 transition-colors text-xs sm:text-sm"
          title={`${queueCount} canciones en la cola de Spotify`}
          aria-label={`Cola de Spotify con ${queueCount} canciones`}
        >
          <ListMusic className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
          <span className="font-medium leading-none">{queueCount}</span>
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        className="w-72 bg-slate-800 border-slate-700 text-white p-3"
        data-testid="spotify-queue-popover"
      >
        <div className="flex items-center gap-2 mb-2">
          <ListMusic className="w-4 h-4 text-green-400" />
          <h4 className="text-sm font-semibold">Próximas en cola</h4>
        </div>
        {upNext.length === 0 ? (
          <p className="text-xs text-slate-400">
            La cola está vacía. Añade canciones desde la lista personalizada o las peticiones.
          </p>
        ) : (
          <ul className="space-y-1.5">
            {upNext.map((track, i) => (
              <li
                key={`${track.id || i}-${i}`}
                className="flex items-center gap-2 text-xs"
                data-testid={`spotify-up-next-${i}`}
              >
                <span className="text-slate-500 w-4 text-right flex-shrink-0">{i + 1}</span>
                <div className="min-w-0">
                  <p className="text-white truncate">{track.title}</p>
                  <p className="text-slate-400 truncate">{track.artist}</p>
                </div>
              </li>
            ))}
          </ul>
        )}
        {queueCount > upNext.length && (
          <p className="text-xs text-slate-500 mt-2 pt-2 border-t border-slate-700">
            +{queueCount - upNext.length} más en la cola
          </p>
        )}
      </PopoverContent>
    </Popover>
  );
};

export default SpotifyQueueBadge;
